/**
 * Crossword — hub (the picker at /crossword).
 *
 * Leads with today's Mini and a Midi, presented as two printed editions on the
 * desk under a confident masthead. Each card links straight to the grid — no
 * intermediate "see the clues first" screen (BUILDER-PLAYBOOK rule 5). A
 * "Play a random grid" button under each card opens a DIFFERENT (non-today's)
 * puzzle for unlimited practice — distinct from the shared daily everyone plays.
 *
 * The composition is editorial: a ruled masthead with the dateline, a short
 * standfirst, then the two edition cards. Light reads as daylight on newsprint;
 * dark as the same sheet under a reading lamp.
 */
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { pickDailyPuzzle } from './dailyPuzzle';
import { cardSpring, HUB_STAGGER_S, hoverSpring, pageSpring } from './motion';
import { puzzlesOfSize } from '@rendever/puzzle-content/crossword';
import type { CrosswordSize } from '@rendever/puzzle-core/crossword';
import { sound } from './sound';

import './CrosswordHub.css';

interface EditionCard {
  size: CrosswordSize;
  title: string;
  eyebrow: string;
  blurb: string;
  /** Mini glyph: the grid shape teased as printed boxes. */
  shape: ReadonlyArray<ReadonlyArray<boolean>>; // true = white cell
}

// Small printed-grid teasers — a 5×5 open square for the Mini, a 7×7 with a few
// blocks for the Midi, and a 9×9 silhouette for the Full. Purely decorative; the
// real grid comes from the corpus.
const MINI_SHAPE: boolean[][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => true));
const MIDI_SHAPE: boolean[][] = [
  [true, true, true, false, true, true, true],
  [true, true, true, true, true, true, true],
  [true, true, true, true, true, true, true],
  [false, true, true, true, true, true, false],
  [true, true, true, true, true, true, true],
  [true, true, true, true, true, true, true],
  [true, true, true, false, true, true, true],
];
// A 9×9 silhouette (a teaser, not the real 11×11) with a symmetric scatter of
// blocks so the Full card reads as a clearly bigger, denser grid.
const FULL_SHAPE: boolean[][] = (() => {
  const n = 9;
  const g = Array.from({ length: n }, () => Array.from({ length: n }, () => true));
  const blocks: ReadonlyArray<readonly [number, number]> = [
    [0, 4],
    [2, 2],
    [2, 6],
    [4, 0],
    [4, 8],
    [3, 4],
    [5, 4],
  ];
  for (const [r, c] of blocks) {
    g[r]![c] = false;
    g[n - 1 - r]![n - 1 - c] = false; // keep the teaser 180°-symmetric
  }
  return g;
})();

const EDITIONS: ReadonlyArray<EditionCard> = [
  {
    size: 'mini',
    title: 'The Mini',
    eyebrow: 'Today, together',
    blurb: 'A small grid to solve over coffee. Everyone in your community gets the same one today.',
    shape: MINI_SHAPE,
  },
  {
    size: 'midi',
    title: 'The Midi',
    eyebrow: 'A longer sit',
    blurb: 'A roomier grid for an unhurried afternoon. The same one for everyone, every day.',
    shape: MIDI_SHAPE,
  },
  {
    size: 'full',
    title: 'Full Crossword',
    eyebrow: 'A bigger grid for a longer sit',
    blurb: 'A full-size grid with more clues to work through. The same one for everyone, every day.',
    shape: FULL_SHAPE,
  },
];

export function CrosswordHub(): React.JSX.Element {
  const navigate = useNavigate();
  const { theme } = usePreferences();

  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('whoosh');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  const open = (size: CrosswordSize, mode: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('whoosh');
    navigate(`/play/crossword?size=${size}&mode=${mode}`);
  };

  return (
    <div className="cw-hub" data-theme={theme}>
      <div className="cw-hub__desk" aria-hidden="true" />
      <div className="cw-hub__sheet">
        <PaperBackdrop />

        <motion.header
          className="cw-hub__masthead"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.05 }}
        >
          <p className="cw-hub__kicker">The Daily</p>
          <h1 className="cw-hub__title">Crossword</h1>
          <div className="cw-hub__rule" aria-hidden="true">
            <span className="cw-hub__rule-line" />
            <span className="cw-hub__rule-dot" />
            <span className="cw-hub__rule-line" />
          </div>
          <p className="cw-hub__dateline">{prettyDate()}</p>
          <p className="cw-hub__standfirst">
            A fresh grid every morning. Fill the squares from the clues — across
            and down cross at every letter. No clock, no streak; solve at your
            own pace, and check or reveal whenever you like.
          </p>
        </motion.header>

        <section className="cw-hub__editions" aria-label="Choose an edition">
          <ul className="cw-hub__cards" role="list">
            {EDITIONS.map((ed, i) => (
              <EditionTile
                key={ed.size}
                edition={ed}
                index={i}
                onPlay={() => open(ed.size, 'daily')}
                onAnother={() => open(ed.size, 'practice')}
              />
            ))}
          </ul>
        </section>

        <p className="cw-hub__colophon" aria-hidden="true">
          Set in Fraunces &amp; Garamond · Printed for the morning
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edition tile
// ---------------------------------------------------------------------------

function EditionTile({
  edition,
  index,
  onPlay,
  onAnother,
}: {
  edition: EditionCard;
  index: number;
  onPlay: () => void;
  onAnother: () => void;
}): React.JSX.Element {
  // Honest availability: only show "Another one" when the corpus has more than
  // one puzzle of this size to reseed into.
  const poolSize = puzzlesOfSize(edition.size).length;
  const hasDaily = pickDailyPuzzle(edition.size) !== null;
  const canReseed = poolSize > 1;

  return (
    <motion.li
      className="cw-hub__card-item"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...cardSpring, delay: 0.16 + index * HUB_STAGGER_S }}
      whileHover={{ y: -5, transition: hoverSpring }}
    >
      <div className="cw-hub__card" data-size={edition.size}>
        <button
          type="button"
          className="cw-hub__card-main"
          onClick={onPlay}
          onPointerEnter={() => sound.play('move')}
          disabled={!hasDaily}
          aria-label={`Play today’s ${edition.title}`}
        >
          <span className="cw-hub__card-eyebrow">{edition.eyebrow}</span>
          <span className="cw-hub__card-title">{edition.title}</span>

          <span className="cw-hub__teaser" aria-hidden="true">
            <GridTeaser shape={edition.shape} />
          </span>

          <span className="cw-hub__card-blurb">{edition.blurb}</span>

          <span className="cw-hub__card-cta" aria-hidden="true">
            {hasDaily ? 'Open today’s grid →' : 'Coming soon'}
          </span>
        </button>

        {canReseed && (
          <button
            type="button"
            className="cw-hub__another"
            onClick={onAnother}
            onPointerEnter={() => sound.play('move')}
            aria-label={`Play a random ${edition.title} — a different grid than today’s, for practice`}
          >
            Play a random grid
          </button>
        )}
      </div>
    </motion.li>
  );
}

/** A printed-grid teaser — pure CSS boxes, mirroring the schema's black squares. */
function GridTeaser({ shape }: { shape: ReadonlyArray<ReadonlyArray<boolean>> }): React.JSX.Element {
  const cols = shape[0]?.length ?? 0;
  return (
    <span
      className="cw-hub__teaser-grid"
      style={{ ['--teaser-cols' as string]: cols }}
    >
      {shape.flatMap((row, r) =>
        row.map((white, c) => (
          <span
            key={`${r}-${c}`}
            className="cw-hub__teaser-cell"
            data-white={white ? 'true' : 'false'}
          />
        )),
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Paper backdrop
// ---------------------------------------------------------------------------

function PaperBackdrop(): React.JSX.Element {
  return (
    <>
      <div className="cw-hub__paper" aria-hidden="true">
        <svg width="100%" height="100%" aria-hidden="true">
          <defs>
            <filter id="cw-hub-grain" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.86" numOctaves="2" seed="13" />
              <feColorMatrix values="0 0 0 0 0.12  0 0 0 0 0.09  0 0 0 0 0.05  0 0 0 0.06 0" />
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#cw-hub-grain)" />
        </svg>
      </div>
      <div className="cw-hub__paper-edge" aria-hidden="true" />
    </>
  );
}

function prettyDate(): string {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
